<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { collaborationApi } from '../services/collaboration';

type CommentItem = {
  id: string; parentId: string | null; body: string; targetType: string; targetId?: string;
  authorName?: string; authorEmail?: string; createdAt: number; updatedAt: number; resolvedAt?: number | null;
  mentions?: Array<{ name?: string; email?: string }>;
};

const route = useRoute();
const router = useRouter();
const cases = ref<any[]>([]);
const selectedCaseId = ref(String(route.query.caseId || ''));
const comments = ref<CommentItem[]>([]);
const activity = ref<any[]>([]);
const includeResolved = ref(true);
const targetType = ref<'case'|'card'|'connection'>((route.query.targetType as any) || 'case');
const targetId = ref(String(route.query.targetId || ''));
const draft = ref('');
const replyDrafts = ref<Record<string, string>>({});
const editingId = ref('');
const editDraft = ref('');
const historyByComment = ref<Record<string, any[]>>({});
const mentionText = ref('');
const loading = ref(false);
const sending = ref(false);
const message = ref('');

const selectedCase = computed(() => cases.value.find(item => item.id === selectedCaseId.value));
const roots = computed(() => comments.value.filter(item => !item.parentId));
const replies = (id: string) => comments.value.filter(item => item.parentId === id);
const visibleComments = computed(() => includeResolved.value ? roots.value : roots.value.filter(item => !item.resolvedAt));

function formatDate(value: number) { return new Date(value).toLocaleString(); }
function author(comment: CommentItem) { return comment.authorName || comment.authorEmail || 'Usuario'; }
function targetLabel(comment: CommentItem) {
  return comment.targetType === 'case' ? 'Caso completo' : `${comment.targetType === 'card' ? 'Tarjeta' : 'Conexión'}${comment.targetId ? ` · ${comment.targetId}` : ''}`;
}
function onSync(event: Event) {
  const detail = (event as CustomEvent).detail || {};
  const realtime = detail.realtimeActivity;
  if (!realtime || realtime.caseId !== selectedCaseId.value) return;
  activity.value.unshift(realtime);
  activity.value = activity.value.slice(0, 50);
  const incoming = realtime.data?.comment as CommentItem | undefined;
  if (!incoming) return;
  const index = comments.value.findIndex(item => item.id === incoming.id);
  if (index >= 0) comments.value[index] = incoming;
  else comments.value.push(incoming);
}
async function loadCases() {
  loading.value = true; message.value = '';
  try {
    cases.value = await collaborationApi.listCases();
    if (!selectedCaseId.value || !cases.value.some(item => item.id === selectedCaseId.value)) selectedCaseId.value = cases.value[0]?.id || '';
    if (selectedCaseId.value) await loadCase();
  } catch (error: any) { message.value = error?.message || 'No se pudieron cargar los casos.'; }
  finally { loading.value = false; }
}
async function loadCase() {
  if (!selectedCaseId.value) return;
  loading.value = true; message.value = '';
  try {
    const [commentResult, activityResult] = await Promise.all([
      collaborationApi.comments(selectedCaseId.value, includeResolved.value),
      collaborationApi.activity(selectedCaseId.value)
    ]);
    comments.value = commentResult.comments || [];
    activity.value = activityResult.events || [];
  } catch (error: any) { message.value = error?.message || 'No se pudieron cargar los comentarios.'; }
  finally { loading.value = false; }
}
async function createComment(parentId?: string) {
  const body = draft.value.trim();
  if (!body || !selectedCaseId.value) return;
  sending.value = true; message.value = '';
  try {
    const result = await collaborationApi.createComment(selectedCaseId.value, {
      body, parentId, targetType: parentId ? undefined : targetType.value, targetId: parentId ? undefined : (targetId.value || undefined),
      mentions: mentionText.value.split(/[,\s]+/).map(value => value.replace(/^@/, '')).filter(Boolean)
    });
    comments.value.push(result.comment);
    draft.value = ''; mentionText.value = '';
  } catch (error: any) { message.value = error?.message || 'No se pudo publicar el comentario.'; }
  finally { sending.value = false; }
}
async function toggleResolved(comment: CommentItem) {
  try {
    const result = await collaborationApi.resolveComment(selectedCaseId.value, comment.id, !comment.resolvedAt);
    const index = comments.value.findIndex(item => item.id === comment.id);
    if (index >= 0) comments.value[index] = result.comment;
  } catch (error: any) { message.value = error?.message || 'No se pudo actualizar el comentario.'; }
}
function startEdit(comment: CommentItem) { editingId.value = comment.id; editDraft.value = comment.body; }
async function saveEdit(comment: CommentItem) {
  if (!editDraft.value.trim()) return;
  try {
    const result = await collaborationApi.updateComment(selectedCaseId.value, comment.id, editDraft.value);
    const index = comments.value.findIndex(item => item.id === comment.id);
    if (index >= 0) comments.value[index] = result.comment;
    editingId.value = '';
  } catch (error: any) { message.value = error?.message || 'No se pudo editar el comentario.'; }
}
async function loadHistory(comment: CommentItem) {
  try { historyByComment.value[comment.id] = (await collaborationApi.commentHistory(selectedCaseId.value, comment.id)).history || []; }
  catch (error: any) { message.value = error?.message || 'No se pudo cargar el historial.'; }
}
async function selectCase(id: string) {
  selectedCaseId.value = id;
  await router.replace({ query: { ...route.query, caseId: id } });
  await loadCase();
}
onMounted(() => { window.addEventListener('mindplace:sync-state', onSync); loadCases(); });
onBeforeUnmount(() => window.removeEventListener('mindplace:sync-state', onSync));
</script>

<template>
  <main class="phase40">
    <header class="phase40__header">
      <div><p class="eyebrow">FASE 40 · 489–495</p><h1>Comentarios y actividad</h1><p>Conversaciones vinculadas al caso, tarjetas y conexiones con historial y resolución.</p></div>
      <div class="phase40__actions"><button type="button" @click="router.push('/')">← Tablero</button><button type="button" @click="router.push({ path: '/fase-39', query: selectedCaseId ? { caseId: selectedCaseId } : {} })">⚡ Tiempo real</button></div>
    </header>
    <div class="phase40__bar">
      <label>Caso <select v-model="selectedCaseId" @change="selectCase(selectedCaseId)"><option value="" disabled>Selecciona un caso</option><option v-for="item in cases" :key="item.id" :value="item.id">{{ item.title || item.id }}</option></select></label>
      <label class="check"><input v-model="includeResolved" type="checkbox" @change="loadCase"> Mostrar resueltos</label>
      <span v-if="selectedCase">{{ selectedCase.title || selectedCase.id }}</span>
    </div>
    <p v-if="message" class="notice" role="status">{{ message }}</p>
    <p v-if="loading" class="muted">Cargando…</p>
    <section v-else class="phase40__layout">
      <section class="panel">
        <div class="panel__head"><h2>Comentarios</h2><span>{{ visibleComments.length }}</span></div>
        <div v-if="!visibleComments.length" class="muted">Todavía no hay comentarios en este caso.</div>
        <article v-for="comment in visibleComments" :key="comment.id" class="comment" :class="{resolved: comment.resolvedAt}">
          <div class="comment__meta"><strong>{{ author(comment) }}</strong><small>{{ formatDate(comment.createdAt) }} · {{ targetLabel(comment) }}</small></div>
          <textarea v-if="editingId === comment.id" v-model="editDraft" rows="3"></textarea><p v-else>{{ comment.body }}</p>
          <small v-if="comment.mentions?.length" class="mentions">Menciones: {{ comment.mentions.map(item => item.name || item.email).join(', ') }}</small>
          <div class="comment__actions"><button v-if="editingId === comment.id" type="button" @click="saveEdit(comment)">Guardar</button><button v-if="editingId === comment.id" type="button" @click="editingId = ''">Cancelar</button><button v-else type="button" @click="startEdit(comment)">Editar</button><button type="button" @click="toggleResolved(comment)">{{ comment.resolvedAt ? 'Reabrir' : 'Resolver' }}</button><button type="button" @click="loadHistory(comment)">Historial</button><span v-if="comment.resolvedAt">Resuelto</span></div>
          <div v-if="historyByComment[comment.id]" class="history"><small v-for="item in historyByComment[comment.id]" :key="item.id">{{ item.action }} · {{ formatDate(item.createdAt) }}</small></div>
          <div v-for="reply in replies(comment.id)" :key="reply.id" class="reply"><strong>{{ author(reply) }}</strong><small>{{ formatDate(reply.createdAt) }}</small><p>{{ reply.body }}</p></div>
          <div class="replyComposer"><input v-model="replyDrafts[comment.id]" placeholder="Responder…"><button type="button" :disabled="!replyDrafts[comment.id]?.trim()" @click="draft = replyDrafts[comment.id] || ''; createComment(comment.id); replyDrafts[comment.id] = ''">Responder</button></div>
        </article>
      </section>
      <aside class="phase40__side">
        <section class="panel composer">
          <div class="panel__head"><h2>Nuevo comentario</h2></div>
          <label>Sobre <select v-model="targetType"><option value="case">Caso</option><option value="card">Tarjeta</option><option value="connection">Conexión</option></select></label>
          <label v-if="targetType !== 'case'">ID del elemento <input v-model="targetId" placeholder="Identificador"></label>
          <label>Texto <textarea v-model="draft" rows="5" placeholder="Escribe un comentario…"></textarea></label>
          <label>Menciones <input v-model="mentionText" placeholder="@usuario o email"></label>
          <button type="button" :disabled="sending || !draft.trim()" @click="createComment()">{{ sending ? 'Publicando…' : 'Publicar comentario' }}</button>
        </section>
        <section class="panel activity"><div class="panel__head"><h2>Actividad</h2><span>{{ activity.length }}</span></div><div v-for="event in activity.slice(0,20)" :key="event.id || event.createdAt" class="activity__item"><strong>{{ event.actorName || event.actorEmail || 'Usuario' }}</strong><span>{{ String(event.action || '').replaceAll('_',' ') }}</span><small>{{ formatDate(Number(event.createdAt)) }}</small></div><p v-if="!activity.length" class="muted">Sin actividad todavía.</p></section>
      </aside>
    </section>
  </main>
</template>

<style scoped>
.phase40{min-height:100vh;padding:2rem clamp(1rem,4vw,4rem);box-sizing:border-box;color:#e5edf8;background:#101827;font-family:system-ui,sans-serif}.phase40__header{display:flex;justify-content:space-between;gap:1rem;align-items:flex-start}.eyebrow{color:#6ee7b7;font-weight:700;letter-spacing:.08em}.phase40 h1{margin:.2rem 0;font-size:clamp(1.8rem,4vw,3rem)}.phase40__actions{display:flex;gap:.5rem;flex-wrap:wrap}.phase40 button,.phase40 select,.phase40 input,.phase40 textarea{border:1px solid #334155;border-radius:.5rem;background:#172235;color:inherit;padding:.65rem}.phase40 button{cursor:pointer;font-weight:700}.phase40 button:disabled{opacity:.5;cursor:not-allowed}.phase40__bar,.panel__head,.comment__meta,.comment__actions{display:flex;align-items:center;gap:.75rem}.phase40__bar{margin:1.5rem 0;flex-wrap:wrap}.phase40 label{display:flex;flex-direction:column;gap:.3rem;font-size:.9rem}.phase40 .check{flex-direction:row;align-items:center}.phase40__layout{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(280px,.8fr);gap:1rem}.panel{background:#172235;border:1px solid #263852;border-radius:.8rem;padding:1rem;min-width:0}.panel__head{justify-content:space-between;border-bottom:1px solid #2c3d57;margin-bottom:1rem}.panel__head h2{margin:.1rem 0 1rem}.comment{border:1px solid #2d405e;border-radius:.7rem;padding:1rem;margin-bottom:.75rem}.comment.resolved{opacity:.62}.comment p{white-space:pre-wrap}.comment__meta{justify-content:space-between}.comment__meta small,.comment__actions,.reply small,.activity__item small,.mentions{color:#9fb0c7}.comment__actions{justify-content:flex-end}.reply{margin:1rem 0 0 1.2rem;padding:.7rem;border-left:2px solid #4ade80}.reply p{margin:.35rem 0}.replyComposer{display:flex;gap:.5rem;margin:1rem 0 0 1.2rem}.replyComposer input{flex:1}.composer{display:grid;gap:.75rem;margin-bottom:1rem}.composer textarea{resize:vertical}.activity__item{display:grid;gap:.2rem;border-bottom:1px solid #2b3b54;padding:.65rem 0}.activity__item span{color:#b7c6d9}.muted{color:#9fb0c7}.notice{padding:.75rem;background:#493b18;border-radius:.5rem}@media(max-width:800px){.phase40__header,.phase40__layout{display:block}.phase40__actions{margin-top:1rem}.phase40__side{margin-top:1rem}}
</style>
